import React from 'react';
import { TrendingUp } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const NextHome = () => (
  <LoanPageTemplate
    guides={[
      { title: 'How Much Can I Borrow?', href: '/blog/borrowing-power', blurb: 'How lenders assess income, expenses and the serviceability buffer.' },
      { title: 'Fixed vs Variable Rates', href: '/blog/fixed-vs-variable', blurb: 'How fixed, variable and split loans compare, and what break costs mean for you.' },
      { title: 'Mortgage Broker vs Bank', href: '/blog/broker-vs-bank', blurb: 'What a broker does, how brokers are paid and when going direct makes sense.' },
    ]}
    icon={TrendingUp}
    path="/next-home"
    title="Next Home Loans"
    subtitle="Upgrade, downsize, or relocate with a finance strategy built around your existing equity. We help you bridge the gap between selling and buying."
    description="Moving to your next home comes with unique financial advantages that first home buyers simply do not have. Your existing equity can fund the deposit on a new property, cover moving costs, and even eliminate Lenders Mortgage Insurance entirely. Whether you are upsizing for a growing family, downsizing after the kids leave, or relocating for work, we structure your finance to make the transition seamless and cost-effective."
    features={[
      {
        title: 'Equity Release & Leverage',
        description: 'We calculate the usable equity in your current home and structure your new loan to maximise your buying power without overextending your budget.'
      },
      {
        title: 'Bridging Finance Solutions',
        description: 'Need to buy before you sell? Bridging loans cover the gap so you can secure your next home without rushing the sale of your current property.'
      },
      {
        title: 'Simultaneous Settlement Coordination',
        description: 'Selling and buying on the same day requires precise timing. We work with both lenders, solicitors, and agents to align settlement dates and avoid costly delays.'
      },
      {
        title: 'Rate Renegotiation on Existing Loans',
        description: 'Before you move, we check whether your current lender will match the market. Sometimes the best deal is renegotiating rather than refinancing to a new bank.'
      },
      {
        title: 'Downsizer Contribution Strategies',
        description: 'Australians aged 55 and over can contribute up to $300,000 per person from the sale of their home into superannuation. We coordinate your loan and super strategy together.'
      },
      {
        title: 'Upgrader Cost Analysis',
        description: 'We map out all costs including stamp duty, agent fees, capital gains implications, and break costs so you can compare the true cost of upgrading versus renovating.'
      }
    ]}
    process={[
      {
        title: 'Equity & Affordability Review',
        description: 'We assess your current property value, outstanding mortgage, and financial position to determine your available equity and new borrowing capacity.'
      },
      {
        title: 'Strategy Selection',
        description: 'Based on your timeline, we recommend whether to sell first, buy first with bridging finance, or pursue simultaneous settlement. Each approach has different risk and cost profiles.'
      },
      {
        title: 'Pre-Approval & Property Search',
        description: 'We secure pre-approval based on your chosen strategy so you can search and make offers with full confidence in your budget.'
      },
      {
        title: 'Settlement Management',
        description: 'We manage all lender communications through settlement, ensuring funds flow correctly whether you are settling one transaction or coordinating two on the same day.'
      }
    ]}
    faqs={[
      {
        q: 'Can I use equity in my current home as a deposit on my next property?',
        a: 'Yes. Most lenders allow you to borrow against the equity in your existing property to fund the deposit and costs on your next home. Usable equity is typically calculated as 80% of your current property value minus your outstanding mortgage balance. For example, if your home is worth $800,000 and you owe $400,000, your usable equity is approximately $240,000.'
      },
      {
        q: 'What is a bridging loan and how does it work?',
        a: 'A bridging loan is a short-term facility that lets you purchase your next home before selling your current one. You make interest-only repayments on the combined debt during the bridging period, which typically lasts up to 6-12 months. Once your existing property sells, the bridging loan is repaid and you continue with a standard home loan on the new property.'
      },
      {
        q: 'Will I pay stamp duty on my next home?',
        a: 'Yes. Unlike first home buyers, subsequent purchasers pay full stamp duty in most states. In NSW, stamp duty on a $1 million property is approximately $40,000. Some states offer concessions for downsizers or pensioners. We calculate the exact duty applicable to your purchase and factor it into your total cost.'
      },
      {
        q: 'Should I sell my current home before buying the next one?',
        a: 'Selling first is the lowest-risk approach because you know exactly how much equity you have. However, it may mean renting between properties. Buying first using bridging finance gives you more time but carries the risk of holding two properties. We help you weigh the financial and lifestyle trade-offs for your situation.'
      },
      {
        q: 'Can I keep my current home as an investment property?',
        a: 'Absolutely. Many Australians convert their existing home into a rental when they upgrade. We restructure your loans so that the investment portion is interest-only and tax-deductible where possible, while your new owner-occupied loan is set up for principal and interest repayments.'
      }
    ]}
    loanType="Home Loan"
    color="from-purple-600 to-purple-800"
  />
);

export default NextHome;
